import React, { useDeferredValue, useMemo, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { addDays, format } from 'date-fns'
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap'
import {
    FaCalendarAlt,
    FaInbox,
    FaRedo,
    FaSearch,
    FaTimes,
} from 'react-icons/fa'
import DateRangeSelector from '../home/dashboard-content/date-range-selector'
import { DataTableStyleSheetManager } from './data-table-style-sheet-manager'
import { ExportRow, TableExportActions, sanitizeExportFilename } from './table-export-actions'
import './report-table.css'

type ReportRow = Record<string, any>

export type ReportColumn = TableColumn<ReportRow> & {
    key: string
    searchable?: boolean
}

type ReportTableProps = {
    getData: (fromDate: string, toDate: string) => Promise<ReportRow[]>
    title: string
    defaultSort: string
    columns: ReportColumn[]
    description?: string
    entityLabel?: string
    pageKey?: string
}

type SelectedRange = {
    startDate: Date
    endDate: Date
}

const reportBlue = '#5b9bd5'

const toExportValue = (value: unknown): string | number | boolean | null | undefined => {
    if (value == null) return ''
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
    if (value instanceof Date) return value.toISOString()

    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

const getExportLabel = (key: string) => key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.toLowerCase() === 'id'
        ? 'ID'
        : `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ')

export const buildExportRows = (columns: ReportColumn[], rows: ReportRow[]): ExportRow[] => {
    const exportableColumns = columns.filter(column => !column.button)
    const configuredKeys = new Set(columns.map(column => column.key))
    const additionalKeys = Array.from(rows.reduce<Set<string>>((keys, row) => {
        Object.keys(row).forEach(key => {
            if (!configuredKeys.has(key)) keys.add(key)
        })
        return keys
    }, new Set()))

    return rows.map((row, rowIndex) => {
        const exportRow = exportableColumns.reduce<ExportRow>((nextExportRow, column, columnIndex) => {
            const label = typeof column.name === 'string' ? column.name : `Column ${columnIndex + 1}`
            const formattedValue = column.format?.(row, rowIndex)
            const selectedValue = typeof formattedValue !== 'undefined'
                ? formattedValue
                : column.selector?.(row, rowIndex)

            nextExportRow[label] = toExportValue(selectedValue)
            return nextExportRow
        }, {})

        additionalKeys.forEach(key => {
            exportRow[getExportLabel(key)] = toExportValue(row[key])
        })

        return exportRow
    })
}

const formatDateForApi = (date: Date) => format(date, 'yyyy-MM-dd')

const getErrorMessage = (error: unknown) => {
    if (error instanceof Error && error.message) return error.message
    return 'The report could not be loaded. Please try again.'
}

const LoadingState = () => (
    <Card className="report-state-card border-0" aria-live="polite" aria-busy="true">
        <Card.Body className="report-loading-state">
            <Spinner animation="border" role="status" />
            <div>
                <div className="fw-semibold text-dark">Building your report</div>
                <div className="small text-muted">Fetching and preparing the selected period…</div>
            </div>
        </Card.Body>
    </Card>
)

const InitialState = () => (
    <Card className="report-state-card border-0">
        <Card.Body className="report-empty-state">
            <span className="report-empty-state-icon" aria-hidden="true">
                <FaCalendarAlt />
            </span>
            <h2>Select a reporting period</h2>
            <p>Open the date picker above, choose a period, and apply it to load the report.</p>
        </Card.Body>
    </Card>
)

export function Table({
    getData,
    title,
    defaultSort,
    columns,
    description = 'Explore, search, and export performance data for the selected period.',
    entityLabel = 'rows',
    pageKey,
}: ReportTableProps) {
    const [data, setData] = useState<ReportRow[]>([])
    const [fetching, setFetching] = useState(false)
    const [didFetch, setDidFetch] = useState(false)
    const [filterText, setFilterText] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [selectedRange, setSelectedRange] = useState<SelectedRange | null>(null)
    const deferredFilterText = useDeferredValue(filterText)

    const searchableKeys = useMemo(() => {
        const configuredKeys = columns.filter(column => column.searchable).map(column => column.key)
        return configuredKeys.length > 0 ? configuredKeys : columns.map(column => column.key)
    }, [columns])

    const filteredData = useMemo(() => {
        const query = deferredFilterText.trim().toLocaleLowerCase()
        if (!query) return data

        return data.filter(item => searchableKeys.some(key => {
            const value = item[key]
            if (value == null) return false
            return String(value).toLocaleLowerCase().includes(query)
        }))
    }, [data, deferredFilterText, searchableKeys])

    const exportRows = useMemo(
        () => buildExportRows(columns, filteredData),
        [columns, filteredData]
    )

    const reportKey = useMemo(
        () => pageKey || `report-${sanitizeExportFilename(title)}`,
        [pageKey, title]
    )

    const loadReport = async (startDate: Date, endDate: Date) => {
        setSelectedRange({ startDate, endDate })
        setFetching(true)
        setError(null)

        try {
            // The report endpoints use an exclusive end date. The picker uses an
            // inclusive end date, so add one day while preserving the API contract.
            const fromDate = formatDateForApi(startDate)
            const toDate = formatDateForApi(addDays(endDate, 1))
            const nextData = await getData(fromDate, toDate)

            if (!Array.isArray(nextData)) {
                throw new Error('The server returned an unexpected report format.')
            }

            setData(nextData)
            setDidFetch(true)
        } catch (loadError) {
            setError(getErrorMessage(loadError))
        } finally {
            setFetching(false)
        }
    }

    const clearSearch = () => setFilterText('')

    const resultLabel = data.length === 1 && entityLabel.endsWith('s')
        ? entityLabel.slice(0, -1)
        : entityLabel
    const resultSummary = filterText.trim()
        ? `${filteredData.length.toLocaleString()} of ${data.length.toLocaleString()} ${entityLabel}`
        : `${data.length.toLocaleString()} ${resultLabel}`

    const minimumTableWidth = columns.length >= 9
        ? '1040px'
        : columns.length >= 7
            ? '820px'
            : '640px'

    const dataTableStyles = useMemo(() => ({
        table: {
            style: {
                width: '100%',
                minWidth: minimumTableWidth,
            },
        },
        rows: {
            style: {
                minHeight: '52px',
                borderBottom: '1px solid #edf0f4',
                color: '#344054',
                fontSize: '0.875rem',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#f8fbff',
                borderBottomColor: '#e3ecf6',
                outline: 'none',
            },
        },
        cells: {
            style: {
                padding: '10px 14px',
                overflowWrap: 'anywhere' as const,
            },
        },
        headRow: {
            style: {
                minHeight: '46px',
                borderBottom: 'none',
                backgroundColor: reportBlue,
            },
        },
        headCells: {
            style: {
                padding: '10px 14px',
                backgroundColor: reportBlue,
                color: '#ffffff',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.025em',
                textTransform: 'uppercase' as const,
                whiteSpace: 'normal' as const,
            },
        },
        pagination: {
            style: {
                width: '100%',
                borderTop: '1px solid #edf0f4',
                color: '#475467',
            },
        },
    }), [minimumTableWidth])

    return (
        <main className="report-page-shell">
            <header className="report-page-header">
                <div className="report-page-heading">
                    <div>
                        <h3 className="mb-1">{title}</h3>
                        <p>{description}</p>
                    </div>
                </div>
            </header>

            <Card className="report-filter-card border-0">
                <Card.Body>
                    <div className="report-filter-layout">
                        <div>
                            <div className="report-section-label">Reporting period</div>
                            <p className="report-filter-help mb-0">
                                Select the date range included in this report.
                            </p>
                        </div>
                        <div className="report-date-selector">
                            <DateRangeSelector
                                onSubmit={loadReport}
                                pageKey={reportKey}
                                defaultLabel="This Month"
                                submitInitialRange
                            />
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {error && (
                <Alert variant="danger" className="report-error-alert" role="alert">
                    <div>
                        <div className="fw-semibold">We couldn’t load this report</div>
                        <div className="small">{error}</div>
                    </div>
                    {selectedRange && (
                        <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            onClick={() => loadReport(selectedRange.startDate, selectedRange.endDate)}
                            disabled={fetching}
                        >
                            <FaRedo aria-hidden="true" />
                            Retry
                        </Button>
                    )}
                </Alert>
            )}

            {fetching && !didFetch ? (
                <LoadingState />
            ) : !didFetch ? (
                <InitialState />
            ) : (
                <Card className="report-results-card border-0">
                    <Card.Header className="report-results-header">
                        <div className="report-results-title">
                            <div className="report-section-label">Results</div>
                            <div className="report-result-count" aria-live="polite">{resultSummary}</div>
                        </div>

                        <div className="report-table-toolbar">
                            <Form.Group className="report-search" controlId={`${reportKey}-search`}>
                                <FaSearch className="report-search-icon" aria-hidden="true" />
                                <Form.Control
                                    type="search"
                                    value={filterText}
                                    onChange={event => setFilterText(event.target.value)}
                                    placeholder={`Search ${entityLabel}`}
                                    aria-label={`Search ${title}`}
                                    disabled={fetching}
                                />
                                {filterText && (
                                    <button
                                        type="button"
                                        className="report-search-clear"
                                        onClick={clearSearch}
                                        aria-label="Clear search"
                                        disabled={fetching}
                                    >
                                        <FaTimes aria-hidden="true" />
                                    </button>
                                )}
                            </Form.Group>

                            <TableExportActions
                                data={exportRows}
                                filename={title}
                                className="report-export-actions d-flex flex-wrap gap-2 justify-content-end"
                                disabled={fetching || exportRows.length === 0}
                            />
                        </div>
                    </Card.Header>

                    <Card.Body className="report-results-body">
                        {fetching && (
                            <div className="report-refresh-overlay" role="status" aria-live="polite">
                                <Spinner animation="border" size="sm" />
                                <span>Refreshing report…</span>
                            </div>
                        )}

                        <div className="report-data-table-frame">
                            <DataTableStyleSheetManager>
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    defaultSortFieldId={defaultSort}
                                    defaultSortAsc={false}
                                    pagination
                                    paginationPerPage={10}
                                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                    paginationComponentOptions={{
                                        rowsPerPageText: 'Rows per page',
                                        rangeSeparatorText: 'of',
                                        selectAllRowsItem: false,
                                    }}
                                    persistTableHead
                                    responsive
                                    highlightOnHover
                                    noHeader
                                    noDataComponent={(
                                        <div className="report-table-empty">
                                            <span className="report-empty-state-icon" aria-hidden="true"><FaInbox /></span>
                                            <div className="fw-semibold text-dark">
                                                {data.length === 0 ? 'No data in this period' : 'No matching results'}
                                            </div>
                                            <div className="small text-muted">
                                                {data.length === 0
                                                    ? 'Try a wider date range to find report data.'
                                                    : 'Adjust or clear your search to see more rows.'}
                                            </div>
                                        </div>
                                    )}
                                    customStyles={dataTableStyles}
                                />
                            </DataTableStyleSheetManager>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </main>
    )
}
